import React, { useState, useEffect } from 'react';
import {
    Plus, Search, BookOpen, Clock, Users, Play, Pause,
    Archive, MoreVertical, Filter, Calendar, CheckSquare,
    Bell, Layers, ArrowRight, Sparkles, BarChart2, Settings,
    ChevronDown, X, CalendarDays
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import CourseTypeModal from '../components/Courses/CourseTypeModal';
import CourseCreationModal from '../components/Courses/CourseCreationModal';
import TimelineEditor from '../components/Courses/TimelineEditor';
import CourseAnalyticsDashboard from '../components/Courses/CourseAnalyticsDashboard';
import EnrollmentManagementModal from '../components/Courses/EnrollmentManagementModal';
import AICourseGenerator from '../components/Courses/AICourseGenerator';
import CourseDeletionModal from '../components/Courses/CourseDeletionModal';
import CoursesAnalyticsDrawer from '../components/Analytics/CoursesAnalyticsDrawer';



const AdminCoursesPage = () => {
    const [activeTab, setActiveTab] = useState('all'); // 'preboarding', 'onboarding', 'all'
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Modal states
    const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
    const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
    const [selectedCourseType, setSelectedCourseType] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState(null);

    // Actions menu state
    const [openActionsMenu, setOpenActionsMenu] = useState(null);

    // Timeline editor state
    const [isTimelineEditorOpen, setIsTimelineEditorOpen] = useState(false);
    const [timelineEditorCourse, setTimelineEditorCourse] = useState(null);

    // Analytics dashboard state
    const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
    const [analyticsCourse, setAnalyticsCourse] = useState(null);

    // Enrollment management state
    const [isEnrollmentOpen, setIsEnrollmentOpen] = useState(false);
    const [enrollmentCourse, setEnrollmentCourse] = useState(null);

    // AI Course Generator state
    const [isAIGeneratorOpen, setIsAIGeneratorOpen] = useState(false);
    const [aiCourseType, setAICourseType] = useState(null);

    // Page Level Analytics State
    const [isPageAnalyticsOpen, setIsPageAnalyticsOpen] = useState(false);

    // Deletion Modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [courseToDelete, setCourseToDelete] = useState(null);

    useEffect(() => {
        fetchCourses();
    }, [activeTab, statusFilter]);

    // Close actions menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setOpenActionsMenu(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const fetchCourses = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = {};

            if (activeTab !== 'all') {
                params.course_type = activeTab;
            }

            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }

            const response = await api.get('/courses', {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            setCourses(response.data.courses || []);
        } catch (error) {
            console.error('Error fetching courses:', error);
            toast.error('Failed to fetch courses');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCourse = () => {
        setSelectedCourse(null);
        setIsTypeModalOpen(true);
    };

    const handleTypeSelected = (type) => {
        setSelectedCourseType(type);
        setIsTypeModalOpen(false);
        setIsCreationModalOpen(true);
    };

    const handleEditCourse = (course) => {
        setSelectedCourse(course);
        setSelectedCourseType(course.course_type);
        setIsCreationModalOpen(true);
    };

    const handleOpenTimeline = (course) => {
        if (course.is_ai_generated) {
            setSelectedCourse(course);
            setAICourseType(course.course_type);
            setIsAIGeneratorOpen(true);
        } else {
            setTimelineEditorCourse(course);
            setIsTimelineEditorOpen(true);
        }
        setOpenActionsMenu(null);
    };

    const handleOpenAnalytics = (course) => {
        setAnalyticsCourse(course);
        setIsAnalyticsOpen(true);
        setOpenActionsMenu(null);
    };

    const handleOpenEnrollment = (course) => {
        setEnrollmentCourse(course);
        setIsEnrollmentOpen(true);
        setOpenActionsMenu(null);
    };

    const handleStatusChange = async (courseId, newStatus) => {
        const loadingToast = toast.loading(`${newStatus === 'active' ? 'Activating' : newStatus === 'paused' ? 'Pausing' : 'Archiving'} course...`);
        try {
            const token = localStorage.getItem('token');
            await api.post(`/courses/${courseId}/status`, { status: newStatus }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Course ${newStatus} successfully`, { id: loadingToast });
            fetchCourses();
        } catch (error) {
            console.error('Error updating course status:', error);
            toast.error('Failed to update course status', { id: loadingToast });
        }
        setOpenActionsMenu(null);
    };

    const handleDeleteCourse = (courseId) => {
        setCourseToDelete(courseId);
        setIsDeleteModalOpen(true);
        setOpenActionsMenu(null);
    };



    const filteredCourses = courses.filter(course =>
        course.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );



    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground mb-2">Activity Centre — Courses</h1>
                <p className="text-sm text-muted-foreground">Create automated onboarding journeys that bundle Tasks, Notices, and Surveys</p>
            </div>

            {/* Tabs */}
            <div className="mb-6 border-b border-border">
                <nav className="flex gap-8">
                    {[
                        { key: 'all', label: 'All Courses' },
                        { key: 'preboarding', label: 'Preboarding' },
                        { key: 'onboarding', label: 'Onboarding' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => { setActiveTab(tab.key); setSearchQuery(''); }}
                            className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === tab.key
                                ? 'border-primary text-primary'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Filter Bar */}
            <div className="bg-card border border-border rounded-lg p-3 mb-5">
                <div className="flex flex-wrap gap-3 items-center justify-between">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search courses..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-3 h-9 border border-border rounded-md text-sm bg-transparent outline-none focus:border-primary transition-colors"
                        />
                    </div>
                    <div className="flex gap-2.5 items-center flex-wrap">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="h-9 border border-border rounded-md px-3 text-sm bg-background outline-none focus:border-primary transition-colors"
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="paused">Paused</option>
                            <option value="draft">Draft</option>
                            <option value="archived">Archived</option>
                        </select>
                        <button
                            onClick={() => setIsPageAnalyticsOpen(true)}
                            className="flex items-center gap-2 bg-background text-indigo-600 border border-indigo-200 hover:bg-indigo-50 shadow-sm h-9 px-3.5 rounded-md text-sm font-semibold transition-colors"
                        >
                            <BarChart2 className="w-3.5 h-3.5" />
                            View Insights
                        </button>
                        <button
                            onClick={() => {
                                const type = activeTab === 'all' ? 'onboarding' : activeTab;
                                setAICourseType(type);
                                setIsAIGeneratorOpen(true);
                            }}
                            className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground h-9 px-3.5 rounded-md text-sm font-semibold transition-colors shadow-sm"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Create Course
                        </button>
                    </div>
                </div>
            </div>

            {/* Courses Card List */}
            <div className="mb-6 space-y-3">
                {loading ? (
                    <div className="p-12 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                        <p className="mt-4 text-muted-foreground text-sm">Loading courses...</p>
                    </div>
                ) : filteredCourses.length === 0 ? (
                    <div className="p-12 text-center bg-card border border-border rounded-lg">
                        <Layers className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                        <h3 className="text-base font-semibold text-foreground mb-2">No courses found</h3>
                        <p className="text-sm text-muted-foreground mb-4">Create your first onboarding course to get started</p>
                        <button
                            onClick={() => {
                                const type = activeTab === 'all' ? 'onboarding' : activeTab;
                                setAICourseType(type);
                                setIsAIGeneratorOpen(true);
                            }}
                            className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-5 py-2 rounded-md font-semibold text-sm hover:bg-primary/90 transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Create Course
                        </button>
                    </div>
                ) : (
                    filteredCourses.map((course) => {
                        const isPreboarding = course.course_type === 'preboarding';
                        const statusStyles = {
                            draft: 'bg-muted text-muted-foreground',
                            active: 'bg-green-100 text-green-700',
                            paused: 'bg-yellow-100 text-yellow-700',
                            archived: 'bg-red-100 text-red-700'
                        };
                        const statusClass = statusStyles[course.status] || statusStyles.draft;

                        return (
                            <div
                                key={course.id}
                                className="bg-card border border-border rounded-lg p-4 transition-all hover:border-primary hover:shadow-sm"
                            >
                                {/* Row 1: Icon + Name + Badges */}
                                <div className="flex items-start gap-3 mb-2.5">
                                    <div className={`mt-0.5 flex-shrink-0 ${isPreboarding ? 'text-violet-600' : 'text-blue-600'}`}>
                                        <BookOpen className="w-4.5 h-4.5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-semibold text-foreground">{course.name}</span>
                                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md ${isPreboarding ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                {isPreboarding ? 'Preboarding' : 'Onboarding'}
                                            </span>
                                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md ${statusClass}`}>
                                                {course.status.charAt(0).toUpperCase() + course.status.slice(1)}
                                            </span>
                                        </div>
                                        {course.description && (
                                            <p className="text-xs text-muted-foreground mt-1 truncate">{course.description}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Row 2: Metadata */}
                                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2.5 pl-7 flex-wrap">
                                    <span className="flex items-center gap-1">
                                        <Users className="w-3 h-3" /> {course.enrolled_count || 0} enrolled
                                    </span>
                                    <span className="text-border">•</span>
                                    <span className="flex items-center gap-1">
                                        <Clock className="w-3 h-3" /> {course.duration_days} days
                                    </span>
                                    <span className="text-border">•</span>
                                    <span className="flex items-center gap-1">
                                        <Layers className="w-3 h-3" /> {course.item_count || 0} items
                                    </span>
                                    <span className="text-border">•</span>
                                    <span className="flex items-center gap-1">
                                        <span className={`w-1.5 h-1.5 rounded-full ${course.auto_enrollment_enabled ? 'bg-green-500' : 'bg-muted-foreground'
                                            }`} />
                                        {course.auto_enrollment_enabled ? (course.auto_enrollment_display || 'Auto') : 'Manual'}
                                    </span>
                                </div>

                                {/* Row 3: Actions */}
                                <div className="flex items-center justify-end gap-2 pl-7 flex-wrap">
                                    <button
                                        onClick={() => handleOpenTimeline(course)}
                                        className="h-8 px-3 text-xs font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-md transition-colors flex items-center gap-1.5"
                                    >
                                        <CalendarDays className="w-3 h-3" /> Timeline
                                    </button>
                                    <button
                                        onClick={() => handleOpenAnalytics(course)}
                                        className="h-8 px-3 text-xs font-medium text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-md transition-colors flex items-center gap-1.5"
                                    >
                                        <BarChart2 className="w-3 h-3" /> Analytics
                                    </button>
                                    <button
                                        onClick={() => handleOpenEnrollment(course)}
                                        className="h-8 px-3 text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors flex items-center gap-1.5"
                                    >
                                        <Users className="w-3 h-3" /> Enrollments
                                    </button>
                                    <div className="relative">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setOpenActionsMenu(openActionsMenu === course.id ? null : course.id); }}
                                            className="h-8 w-8 flex items-center justify-center bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-md transition-colors"
                                        >
                                            <MoreVertical className="w-3.5 h-3.5" />
                                        </button>
                                        {openActionsMenu === course.id && (
                                            <div className="absolute right-0 bottom-9 w-44 bg-popover border border-border rounded-lg shadow-lg py-1 z-20">
                                                <button
                                                    onClick={() => handleEditCourse(course)}
                                                    className="w-full px-3 py-2 text-left text-xs font-medium text-foreground hover:bg-muted transition-colors flex items-center gap-2"
                                                >
                                                    <Settings className="w-3.5 h-3.5" /> Settings
                                                </button>
                                                {course.status !== 'active' && course.status !== 'archived' && (
                                                    <button
                                                        onClick={() => handleStatusChange(course.id, 'active')}
                                                        className="w-full px-3 py-2 text-left text-xs font-medium text-green-600 hover:bg-muted transition-colors flex items-center gap-2"
                                                    >
                                                        <Play className="w-3.5 h-3.5" /> Activate
                                                    </button>
                                                )}
                                                {course.status === 'active' && (
                                                    <button
                                                        onClick={() => handleStatusChange(course.id, 'paused')}
                                                        className="w-full px-3 py-2 text-left text-xs font-medium text-amber-600 hover:bg-muted transition-colors flex items-center gap-2"
                                                    >
                                                        <Pause className="w-3.5 h-3.5" /> Pause
                                                    </button>
                                                )}
                                                {course.status === 'paused' && (
                                                    <button
                                                        onClick={() => handleStatusChange(course.id, 'active')}
                                                        className="w-full px-3 py-2 text-left text-xs font-medium text-green-600 hover:bg-muted transition-colors flex items-center gap-2"
                                                    >
                                                        <Play className="w-3.5 h-3.5" /> Resume
                                                    </button>
                                                )}
                                                {course.status !== 'archived' && (
                                                    <button
                                                        onClick={() => handleStatusChange(course.id, 'archived')}
                                                        className="w-full px-3 py-2 text-left text-xs font-medium text-destructive hover:bg-muted transition-colors flex items-center gap-2"
                                                    >
                                                        <Archive className="w-3.5 h-3.5" /> Archive
                                                    </button>
                                                )}
                                                <div className="my-1 border-t border-border" />
                                                <button
                                                    onClick={() => handleDeleteCourse(course.id)}
                                                    className="w-full px-3 py-2 text-left text-xs font-medium text-destructive hover:bg-muted transition-colors flex items-center gap-2"
                                                >
                                                    <X className="w-3.5 h-3.5" /> Delete
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Course Type Selection Modal */}
            <CourseTypeModal
                isOpen={isTypeModalOpen}
                onClose={() => setIsTypeModalOpen(false)}
                onSelectType={handleTypeSelected}
                onSelectAI={(type) => {
                    setIsTypeModalOpen(false);
                    setAICourseType(type);
                    setIsAIGeneratorOpen(true);
                }}
            />


            {/* Course Creation/Edit Modal */}
            <CourseCreationModal
                isOpen={isCreationModalOpen}
                onClose={() => {
                    setIsCreationModalOpen(false);
                    setSelectedCourse(null);
                    setSelectedCourseType(null);
                }}
                onSuccess={() => {
                    fetchCourses();
                    setIsCreationModalOpen(false);
                    setSelectedCourse(null);
                    setSelectedCourseType(null);
                }}
                courseType={selectedCourseType}
                course={selectedCourse}
            />

            {/* Timeline Editor */}
            <TimelineEditor
                isOpen={isTimelineEditorOpen}
                onClose={() => {
                    setIsTimelineEditorOpen(false);
                    setTimelineEditorCourse(null);
                }}
                course={timelineEditorCourse}
                onSuccess={() => {
                    fetchCourses();
                }}
            />

            {/* Analytics Dashboard */}
            <CourseAnalyticsDashboard
                isOpen={isAnalyticsOpen}
                onClose={() => {
                    setIsAnalyticsOpen(false);
                    setAnalyticsCourse(null);
                }}
                course={analyticsCourse}
            />

            {/* Enrollment Management Modal */}
            <EnrollmentManagementModal
                isOpen={isEnrollmentOpen}
                onClose={() => {
                    setIsEnrollmentOpen(false);
                    setEnrollmentCourse(null);
                }}
                course={enrollmentCourse}
            />

            {/* AI Course Generator */}
            <AICourseGenerator
                isOpen={isAIGeneratorOpen}
                onClose={() => {
                    setIsAIGeneratorOpen(false);
                    setAICourseType(null);
                    setSelectedCourse(null);
                }}
                courseType={aiCourseType || 'onboarding'}
                initialCourseData={selectedCourse}
                onSuccess={() => {
                    setIsAIGeneratorOpen(false);
                    setAICourseType(null);
                    setSelectedCourse(null);
                    fetchCourses();
                }}
            />

            {/* Course Deletion Modal */}
            <CourseDeletionModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setCourseToDelete(null);
                }}
                courseId={courseToDelete}
                onSuccess={() => {
                    fetchCourses();
                }}
            />

            {/* Page Level Analytics Drawer */}
            <CoursesAnalyticsDrawer
                isOpen={isPageAnalyticsOpen}
                onClose={() => setIsPageAnalyticsOpen(false)}
            />
        </div>

    );
};

export default AdminCoursesPage;
